import { useCallback, useState } from 'react';

import Button from '@atlaskit/button/default/button';

import Image from '@atlaskit/image';

import { GoalsIcon } from '@atlaskit/logo';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import Modal from '@atlaskit/modal-dialog/modal-dialog';

import { CloseButton } from '@atlaskit/modal-dialog/close-button';

import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { useModal } from '@atlaskit/modal-dialog/hooks';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Flex } from '@atlaskit/primitives/compiled/flex';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Text } from '@atlaskit/primitives/compiled/text';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { cssMap } from '@compiled/react';

import Admin from './assets/Admin.png';

import User from './assets/User.png';

import Heading from '@atlaskit/heading/heading';

import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	leftColumn: {
		height: '100%',
		justifyContent: 'space-between',
		paddingBlockStart: token('space.400'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.400'),
		paddingInlineStart: token('space.400'),
	},
	imageContainer: {
		position: 'relative',
		display: 'flex',
		alignSelf: 'end',
		alignChildren: 'end',
		minWidth: '400px',
	},
	crossButtonStyles: {
		position: 'absolute',
		right: token('space.200'),
		top: token('space.200'),
	},
	container: {
		position: 'relative',
	},
	footer: {
		display: 'flex',
		flexDirection: 'row-reverse',
	},
});

const CustomCloseButton = () => {
	const { onClose } = useModal();
	return (
		<Box xcss={styles.crossButtonStyles}>
			<CloseButton onClick={onClose} />
		</Box>
	);
};

interface Props {
	isAdmin?: boolean;
	settingsPageRedirect?: () => void;
	onMoreButtonClicked?: () => void;
	dismiss: () => void;
}

export const SuccessMeasuresChangeboardingBanner = ({
	isAdmin = true,
	settingsPageRedirect,
	onMoreButtonClicked,
	dismiss,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const { onClosed, onCtaActioned, onDeclined } = usePostOfficeUserInteractions();
	const [isOpen, setIsOpen] = useState(true);

	const dismissModal = useCallback(() => {
		dismiss();
		setIsOpen(false);
	}, [dismiss]);

	const handleClose = useCallback(() => {
		void onClosed({
			interactionIdentifier: 'clickToDismissSuccessMeasuresModal',
		});
		dismissModal();
	}, [dismissModal, onClosed]);

	const handlePrimaryAction = useCallback(() => {
		if (isAdmin) {
			void onCtaActioned({
				interactionIdentifier: 'viewGoalTypes',
			});
			dismissModal();
			settingsPageRedirect?.();
			return;
		}

		void onDeclined({
			interactionIdentifier: 'dismissSuccessMeasuresModal',
		});
		dismissModal();
	}, [dismissModal, isAdmin, onCtaActioned, onDeclined, settingsPageRedirect]);

	const handleMoreAction = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'moreOnGoalTypes',
		});
		if (!isAdmin) {
			dismissModal();
		}
		onMoreButtonClicked?.();
	}, [dismissModal, isAdmin, onCtaActioned, onMoreButtonClicked]);

	const admin = {
		header: formatMessage(messages.adminModalHeader),
		introduction: (
			<FormattedMessage
				defaultMessage="You can now customize the <bold>types of goals</bold> your organization uses and add <bold>success measures</bold> to track the goal’s progress across several metrics."
				id="success-measures-changeboarding-modal.goals-screen-space-flags.admin-content-introduction"
				description="Introduction around goal types"
				values={{
					bold: (text) => <Text weight="bold">{text}</Text>,
				}}
			/>
		),
		content: formatMessage(messages.adminContent),
	};

	const user = {
		header: formatMessage(messages.userModalHeader),
		introduction: formatMessage(messages.userContentIntroduction),
		content: formatMessage(messages.userContent),
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal width={800} onClose={handleClose}>
					<Inline spread="space-between" xcss={styles.container}>
						<Flex xcss={styles.leftColumn} direction="column">
							<Stack space="space.600">
								<GoalsIcon size="small" />
								<Stack space="space.300">
									<Heading size="medium">{isAdmin ? admin.header : user.header}</Heading>
									<Stack space="space.150">
										<Text>{isAdmin ? admin.introduction : user.introduction}</Text>
										<Text>{isAdmin ? admin.content : user.content}</Text>
									</Stack>
								</Stack>
							</Stack>
							<Inline alignInline="end" space="space.100" xcss={styles.footer}>
								<Button appearance="primary" onClick={handlePrimaryAction}>
									{isAdmin
										? formatMessage(messages.viewGoalTypesButton)
										: formatMessage(messages.dismissButton)}
								</Button>
								<Button appearance="subtle" onClick={handleMoreAction}>
									{formatMessage(messages.moreButton)}
								</Button>
							</Inline>
						</Flex>
						<Box xcss={styles.imageContainer}>
							<CustomCloseButton />
							<Image src={isAdmin ? Admin : User} alt="" />
						</Box>
					</Inline>
				</Modal>
			)}
		</ModalTransition>
	);
};
